import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Form, Modal } from 'react-bootstrap';
import AppContext from 'context/Context';
import { post, patch } from 'services/bs-sector-measurable/sectorService';
import {
  swalToastError,
  swalToastSuccess
} from 'components/utilities/SwalToastUtil';

const buildInitialFormData = (sector, nextOrderNo) => ({
  name: sector.name ?? '',
  orderNo: sector.orderNo ?? sector.value2 ?? nextOrderNo
});

export default function SicSectorModal({
  sector,
  kpiBoardId,
  dailyScheduleTypeId,
  nextOrderNo,
  handleClose,
  handleSaved
}) {
  const {
    config: { isDark }
  } = useContext(AppContext);
  const isEditing = Boolean(sector.id);
  const [formData, setFormData] = useState(() =>
    buildInitialFormData(sector, nextOrderNo)
  );
  const [isSaving, setIsSaving] = useState(false);

  const updateField = (field, value) =>
    setFormData(previous => ({ ...previous, [field]: value }));

  const handleSubmit = async event => {
    event.preventDefault();
    if (!dailyScheduleTypeId) {
      swalToastError('Daily schedule type is not configured.');
      return;
    }
    const request = {
      name: formData.name.trim(),
      orderNo: Number(formData.orderNo) || 0,
      scheduleType: dailyScheduleTypeId
    };

    setIsSaving(true);
    const response = isEditing
      ? await patch(sector.id, request)
      : await post({ ...request, kpiId: kpiBoardId });
    setIsSaving(false);

    if (!response?.success) {
      swalToastError(response?.message || 'Failed to save sector.');
      return;
    }
    swalToastSuccess(isEditing ? 'Sector updated' : 'Sector created');
    handleSaved();
  };

  return (
    <Modal show onHide={handleClose} contentClassName="border">
      <Form onSubmit={handleSubmit}>
        <Modal.Header
          closeButton
          closeVariant={isDark ? 'white' : undefined}
          className="bg-light px-card border-bottom-0"
        >
          <Modal.Title as="h5">
            {isEditing ? 'Edit' : 'Create'} Sector
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-card">
          <Form.Group className="mb-3">
            <Form.Label className="fs-0 required">Name</Form.Label>
            <Form.Control
              required
              value={formData.name}
              onChange={e => updateField('name', e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="fs-0 required">Order No</Form.Label>
            <Form.Control
              type="number"
              required
              value={formData.orderNo}
              onChange={e => updateField('orderNo', e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="bg-light px-card border-top-0">
          <Button
            variant="primary"
            type="submit"
            className="px-4 mx-0"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

SicSectorModal.propTypes = {
  sector: PropTypes.object.isRequired,
  kpiBoardId: PropTypes.number.isRequired,
  dailyScheduleTypeId: PropTypes.number,
  nextOrderNo: PropTypes.number,
  handleClose: PropTypes.func.isRequired,
  handleSaved: PropTypes.func.isRequired
};
